/**
 * @file projectTemplatesService.ts
 * @brief Fetches project templates from backend for Feature 05 (Use template).
 */

import apiClient, { unwrapResponse } from './apiClient';
import { API_ENDPOINTS } from './endpoints';

export type TemplateCategory = 'all' | 'software' | 'marketing' | 'business' | 'research';

export interface ProjectTemplateFromApi {
  id: string;
  name: string;
  description: string;
  category: TemplateCategory;
  icon: string;
  color: string;
  estimatedDuration: string;
  taskCount: number;
  milestoneCount: number;
  tags: string[];
  phases: { name: string; tasks: string[]; duration: string }[];
  defaultTasks: { name: string; description?: string; estimatedHours: number; phase: string }[];
  defaultMilestones: { name: string; description?: string; phaseIndex: number }[];
}

const CATEGORY_ICON: Record<string, string> = {
  software: 'FileCode',
  marketing: 'Layout',
  business: 'Briefcase',
  research: 'Lightbulb',
};
const CATEGORY_COLOR: Record<string, string> = {
  software: '#2563eb',
  marketing: '#f59e0b',
  business: '#0891b2',
  research: '#8b5cf6',
};

function mapListEntry(raw: Record<string, unknown>): ProjectTemplateFromApi {
  const category = (raw.category as string) || 'business';
  const days = Number(raw.estimatedDurationDays ?? 0);
  return {
    id: String(raw.id ?? ''),
    name: String(raw.name ?? ''),
    description: String(raw.description ?? ''),
    category: category as TemplateCategory,
    icon: CATEGORY_ICON[category] ?? 'FolderKanban',
    color: CATEGORY_COLOR[category] ?? '#64748b',
    estimatedDuration: days > 0 ? `${days} days` : '—',
    taskCount: 0,
    milestoneCount: 0,
    tags: Array.isArray(raw.tags) ? (raw.tags as string[]) : [],
    phases: [],
    defaultTasks: [],
    defaultMilestones: [],
  };
}

function mapFullTemplate(raw: Record<string, unknown>): ProjectTemplateFromApi {
  const listMapped = mapListEntry(raw);
  const milestones = Array.isArray(raw.milestones) ? (raw.milestones as Record<string, unknown>[]) : [];
  const tasks = Array.isArray(raw.tasks) ? (raw.tasks as Record<string, unknown>[]) : [];
  const phaseNames = milestones.map((m: Record<string, unknown>) => String(m.name ?? ''));
  const defaultMilestones = milestones.map((m: Record<string, unknown>, idx: number) => ({
    name: String(m.name ?? ''),
    description: m.description != null ? String(m.description) : undefined,
    phaseIndex: idx,
  }));
  const defaultTasks = tasks.map((t: Record<string, unknown>) => {
    const milestoneId = t.milestoneTemplateId as string | undefined;
    const phaseIndex = milestoneId ? milestones.findIndex((m: Record<string, unknown>) => m.id === milestoneId) : 0;
    const phase = phaseIndex >= 0 && phaseNames[phaseIndex] ? phaseNames[phaseIndex] : 'General';
    return {
      name: String(t.title ?? ''),
      description: t.description != null ? String(t.description) : undefined,
      estimatedHours: Number(t.estimatedHours ?? 0),
      phase,
    };
  });
  const phases = milestones.map((m: Record<string, unknown>, idx: number) => {
    const phaseTasks = tasks.filter(
      (t: Record<string, unknown>) => (t.milestoneTemplateId as string) === (m.id as string)
    );
    const daysFromStart = Number(m.daysFromStart ?? 0);
    const next = milestones[idx + 1];
    const nextDays = next != null ? Number((next as Record<string, unknown>).daysFromStart ?? 0) : daysFromStart + 7;
    const durationDays = Math.max(0, nextDays - daysFromStart);
    return {
      name: String(m.name ?? ''),
      tasks: phaseTasks.map((t: Record<string, unknown>) => String(t.title ?? '')),
      duration: `${durationDays} days`,
    };
  });
  return {
    ...listMapped,
    taskCount: tasks.length,
    milestoneCount: milestones.length,
    defaultTasks,
    defaultMilestones,
    phases,
  };
}

export const projectTemplatesService = {
  async getList(): Promise<ProjectTemplateFromApi[]> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.PROJECT_TEMPLATES.LIST);
      const data = unwrapResponse<Record<string, unknown>[]>(response);
      return Array.isArray(data) ? data.map(mapListEntry) : [];
    } catch {
      return [];
    }
  },

  async getById(id: string): Promise<ProjectTemplateFromApi | null> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.PROJECT_TEMPLATES.GET(id));
      const data = unwrapResponse<Record<string, unknown>>(response);
      return data ? mapFullTemplate(data) : null;
    } catch {
      return null;
    }
  },
};

export default projectTemplatesService;
